import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Type, Palette, Printer, Check, Wifi, WifiOff, AlertCircle, Store, Receipt, ClipboardCheck, CircleCheck, CircleX } from 'lucide-react';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useNegocio } from '../contexts/NegocioContext';
import { REGIMENES_FISCALES } from '../constants/catalogosSat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_SIZES = [
  { id: 'sm', label: 'Pequeño',   preview: 'text-xs',  sample: 'Aa' },
  { id: 'md', label: 'Normal',    preview: 'text-sm',  sample: 'Aa' },
  { id: 'lg', label: 'Grande',    preview: 'text-base',sample: 'Aa' },
  { id: 'xl', label: 'Muy grande',preview: 'text-lg',  sample: 'Aa' },
];

const BG_THEMES = [
  { id: 'zinc',  label: 'Gris',      color: '#f4f4f5', border: '#d4d4d8' },
  { id: 'slate', label: 'Azul gris', color: '#f1f5f9', border: '#cbd5e1' },
  { id: 'stone', label: 'Piedra',    color: '#f5f5f4', border: '#d6d3d1' },
  { id: 'sky',   label: 'Cielo',     color: '#e0f2fe', border: '#7dd3fc' },
  { id: 'warm',  label: 'Cálido',    color: '#fef9c3', border: '#fde047' },
];

function ChecklistItem({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2">
      {ok
        ? <CircleCheck className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
        : <CircleX className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
      }
      <div>
        <p className={`text-sm font-medium ${ok ? 'text-zinc-800' : 'text-zinc-700'}`}>{label}</p>
        {hint && <p className="text-xs text-zinc-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Icon className="w-4 h-4 text-zinc-500" />
        <span className="font-semibold text-zinc-800 text-sm">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Configuracion() {
  const navigate = useNavigate();
  const { prefs, update } = usePreferences();
  const { user } = useAuth();
  const { nombreNegocio, iconoNegocio, updateConfig } = useNegocio();

  const [nombreInput, setNombreInput] = useState(nombreNegocio);
  const [iconoInput, setIconoInput] = useState(iconoNegocio);
  const [negocioSaved, setNegocioSaved] = useState(false);
  const [nombreError, setNombreError] = useState('');

  const ICONOS_RAPIDOS = ['🍗', '🐔', '🍖', '🥩', '🔥', '⭐', '🏪', '🍽️', '🌟', '💛'];

  const saveNegocio = async () => {
    const trimmed = nombreInput.trim();
    if (!trimmed) { setNombreError('El nombre no puede estar vacío'); return; }
    setNombreError('');
    try {
      await updateConfig({ nombre_negocio: trimmed, icono_negocio: iconoInput });
      setNegocioSaved(true);
      setTimeout(() => setNegocioSaved(false), 2000);
    } catch {
      setNombreError('Error al guardar');
    }
  };

  const [fiscal, setFiscal] = useState({ rfc_emisor: '', razon_social_emisor: '', regimen_fiscal_emisor: '' });
  const [fiscalSaved, setFiscalSaved] = useState(false);
  const [fiscalError, setFiscalError] = useState('');
  const [savingFiscal, setSavingFiscal] = useState(false);

  const [facturamaEnv, setFacturamaEnv] = useState('sandbox');
  const [facturamaConfigurado, setFacturamaConfigurado] = useState(false);
  const [puntosConFacturacion, setPuntosConFacturacion] = useState(null); // null = aún no carga

  const [emisores, setEmisores] = useState([]);
  const [creandoEmisor, setCreandoEmisor] = useState(false);
  const [emisorError, setEmisorError] = useState('');

  const loadEmisores = () => {
    axios.get(`${API}/emisores`)
      .then(({ data }) => setEmisores(data || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (user?.role !== 'propietario') return;
    axios.get(`${API}/configuracion`)
      .then(({ data }) => {
        setFiscal({
          rfc_emisor: data.rfc_emisor || '',
          razon_social_emisor: data.razon_social_emisor || '',
          regimen_fiscal_emisor: data.regimen_fiscal_emisor || '',
        });
        setFacturamaEnv(data.facturama_env || 'sandbox');
        setFacturamaConfigurado(!!data.facturama_configurado);
      })
      .catch(() => {});
    axios.get(`${API}/puntos-venta`)
      .then(({ data }) => setPuntosConFacturacion((data || []).filter(p => p.facturacion_habilitada).length))
      .catch(() => setPuntosConFacturacion(0));
    loadEmisores();
  }, [user]);

  const fiscalCompleto = !!(fiscal.rfc_emisor && fiscal.razon_social_emisor && fiscal.regimen_fiscal_emisor);
  const enProduccion = facturamaEnv === 'production';
  const hayEmisores = emisores.length > 0;
  const listoParaProduccion = enProduccion && facturamaConfigurado && hayEmisores && puntosConFacturacion > 0;

  const crearEmisor = async () => {
    setEmisorError('');
    if (!fiscalCompleto) { setEmisorError('Completa RFC, razón social y régimen fiscal primero'); return; }
    setCreandoEmisor(true);
    try {
      await axios.post(`${API}/emisores`, {
        rfc: fiscal.rfc_emisor,
        razon_social: fiscal.razon_social_emisor,
        regimen_fiscal: fiscal.regimen_fiscal_emisor,
      });
      loadEmisores();
    } catch (err) {
      setEmisorError(err.response?.data?.detail || 'Error al crear el emisor');
    } finally {
      setCreandoEmisor(false);
    }
  };

  const saveFiscal = async () => {
    setFiscalError('');
    setSavingFiscal(true);
    try {
      await updateConfig(fiscal);
      setFiscalSaved(true);
      setTimeout(() => setFiscalSaved(false), 2000);
    } catch (err) {
      setFiscalError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingFiscal(false);
    }
  };

  const [printerIp, setPrinterIp] = useState(prefs.printerIp);
  const [printerPort, setPrinterPort] = useState(prefs.printerPort);
  const [printerEnabled, setPrinterEnabled] = useState(prefs.printerEnabled);
  const [bridgeUrl, setBridgeUrl] = useState(prefs.bridgeUrl || 'http://localhost:3333');
  const [printerSaved, setPrinterSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // null | 'ok' | 'error'
  const [testError, setTestError] = useState('');

  const savePrinter = () => {
    update({ printerIp, printerPort, printerEnabled, bridgeUrl });
    setPrinterSaved(true);
    setTimeout(() => setPrinterSaved(false), 2000);
  };

  const testPrint = async () => {
    const ip = printerIp.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
    const bridge = bridgeUrl.trim().replace(/\/$/, '');
    if (!ip) { setTestResult('error'); setTestError('Ingresa la IP de la impresora'); return; }
    if (!bridge) { setTestResult('error'); setTestError('Ingresa la URL del puente local'); return; }
    setTesting(true);
    setTestResult(null);
    setTestError('');
    const content =
      '================================\n' +
      '    PRUEBA DE IMPRESION\n' +
      '================================\n' +
      'Sistema POS Pollerias\n' +
      `Impresora: ${ip}:${printerPort || 9100}\n` +
      `Fecha: ${new Date().toLocaleString('es-MX')}\n` +
      '================================\n';
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${bridge}/`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 6000;
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && json.ok) resolve(json);
            else reject(new Error(json.error || `Error ${xhr.status}`));
          } catch { reject(new Error('Respuesta inválida del puente')); }
        };
        xhr.onerror = () => reject(new Error('No se pudo conectar al puente local'));
        xhr.ontimeout = () => reject(new Error('Tiempo de espera agotado'));
        xhr.send(JSON.stringify({ ip, port: parseInt(printerPort) || 9100, content }));
      });
      setTestResult('ok');
    } catch (e) {
      setTestResult('error');
      setTestError(e.message || 'No se pudo conectar al puente local');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Configuración</h1>
            <p className="text-xs text-zinc-500">Preferencias de este dispositivo</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 pb-24">

        {/* ── Identidad del negocio (solo propietario) ── */}
        {user?.role === 'propietario' && (
          <Section icon={Store} title="Identidad del negocio">
            <p className="text-xs text-zinc-500 mb-4">
              El nombre e ícono aparecen en la pantalla de inicio de sesión y en la barra de navegación.
            </p>

            {/* Preview */}
            <div className="flex flex-col items-center py-4 mb-4 bg-zinc-50 rounded-xl border border-zinc-200">
              <span className="text-5xl leading-none mb-2">{iconoInput}</span>
              <span className="font-bold text-zinc-900 text-base">{nombreInput || 'Nombre del negocio'}</span>
            </div>

            {/* Nombre */}
            <label className="text-xs text-zinc-500 font-medium mb-1 block">Nombre</label>
            <input
              type="text"
              value={nombreInput}
              onChange={e => setNombreInput(e.target.value)}
              placeholder="Ej. Pollería El Sabroso"
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-600 mb-4"
            />

            {/* Ícono */}
            <label className="text-xs text-zinc-500 font-medium mb-2 block">Ícono</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {ICONOS_RAPIDOS.map(ic => (
                <button
                  key={ic}
                  onClick={() => setIconoInput(ic)}
                  className={`w-10 h-10 text-2xl rounded-lg border-2 flex items-center justify-center transition-all ${iconoInput === ic ? 'border-zinc-900 bg-zinc-100' : 'border-zinc-200'}`}
                >
                  {ic}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={iconoInput}
              onChange={e => setIconoInput(e.target.value)}
              placeholder="O escribe cualquier emoji"
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-600 mb-4"
            />

            <button
              onClick={saveNegocio}
              className="w-full h-11 bg-zinc-900 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              {negocioSaved ? <><Check className="w-4 h-4" /> Guardado</> : 'Guardar cambios'}
            </button>
            {nombreError && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{nombreError}
              </p>
            )}
          </Section>
        )}

        {/* ── Checklist de producción para facturación (solo propietario) ── */}
        {user?.role === 'propietario' && (
          <Section icon={ClipboardCheck} title="Checklist de producción (Facturación)">
            <p className="text-xs text-zinc-500 mb-4">
              Verifica que todo esté listo antes de empezar a timbrar facturas reales.
            </p>

            <div className={`mb-4 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${
              listoParaProduccion ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {listoParaProduccion
                ? <><CircleCheck className="w-4 h-4 shrink-0" />Listo para facturar en producción</>
                : <><AlertCircle className="w-4 h-4 shrink-0" />Aún no está listo para producción</>
              }
            </div>

            <div className="space-y-3">
              <ChecklistItem
                ok={enProduccion}
                label={enProduccion ? 'Facturama en modo producción' : 'Facturama en modo prueba (sandbox)'}
                hint={!enProduccion ? 'Se cambia en el servidor: variable FACTURAMA_ENV=production en el .env, con las credenciales de tu cuenta de producción.' : null}
              />
              <ChecklistItem
                ok={facturamaConfigurado}
                label={facturamaConfigurado ? 'Credenciales de Facturama configuradas' : 'Faltan credenciales de Facturama'}
                hint={!facturamaConfigurado ? 'Configura FACTURAMA_USER y FACTURAMA_PASSWORD en el .env del servidor.' : null}
              />
              <ChecklistItem
                ok={hayEmisores}
                label={hayEmisores ? `${emisores.length} emisor(es) (RFC) creado(s)` : 'No hay ningún emisor (RFC) creado'}
                hint={!hayEmisores ? 'Completa los datos fiscales de abajo y crea el emisor.' : null}
              />
              <ChecklistItem
                ok={puntosConFacturacion > 0}
                label={puntosConFacturacion > 0
                  ? `${puntosConFacturacion} punto(s) de venta con facturación habilitada`
                  : 'Ningún punto de venta tiene facturación habilitada'}
                hint={puntosConFacturacion === 0 ? 'Ve a Puntos de Venta, actívala y asigna el emisor (requiere código postal de esa sucursal).' : null}
              />
            </div>

            <p className="text-xs text-zinc-400 mt-4">
              El CSD (certificado de sello digital) no se gestiona desde aquí: se sube directamente en el portal de Facturama, asociado a tu cuenta de producción — por seguridad, esta app nunca almacena ni maneja esos archivos.
            </p>
          </Section>
        )}

        {/* ── Datos fiscales del emisor (solo propietario) ── */}
        {user?.role === 'propietario' && (
          <Section icon={Receipt} title="Datos fiscales (facturación)">
            <p className="text-xs text-zinc-500 mb-4">
              Se usan como emisor en todas las facturas (CFDI), sin importar desde qué punto de venta se generen.
            </p>

            <label className="text-xs text-zinc-500 font-medium mb-1 block">RFC</label>
            <input
              type="text"
              value={fiscal.rfc_emisor}
              onChange={e => setFiscal({ ...fiscal, rfc_emisor: e.target.value.toUpperCase() })}
              placeholder="Ej. ABC010101AB1"
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:border-zinc-600 mb-4"
              data-testid="input-rfc-emisor"
            />

            <label className="text-xs text-zinc-500 font-medium mb-1 block">Razón social</label>
            <input
              type="text"
              value={fiscal.razon_social_emisor}
              onChange={e => setFiscal({ ...fiscal, razon_social_emisor: e.target.value })}
              placeholder="Ej. Pollería El Sabroso S.A. de C.V."
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:border-zinc-600 mb-4"
              data-testid="input-razon-social-emisor"
            />

            <label className="text-xs text-zinc-500 font-medium mb-1 block">Régimen fiscal</label>
            <select
              value={fiscal.regimen_fiscal_emisor}
              onChange={e => setFiscal({ ...fiscal, regimen_fiscal_emisor: e.target.value })}
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm bg-white focus:outline-none focus:border-zinc-600 mb-4"
              data-testid="input-regimen-emisor"
            >
              <option value="">Selecciona un régimen</option>
              {REGIMENES_FISCALES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            <button
              onClick={saveFiscal}
              disabled={savingFiscal}
              className="w-full h-11 bg-zinc-900 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-save-fiscal"
            >
              {fiscalSaved ? <><Check className="w-4 h-4" /> Guardado</> : 'Guardar datos fiscales'}
            </button>
            {fiscalError && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{fiscalError}
              </p>
            )}
            <p className="text-xs text-zinc-400 mt-3">
              El código postal de cada sucursal se configura por separado en Puntos de Venta y se usa como lugar de expedición de sus facturas.
            </p>
          </Section>
        )}

        {/* ── Emisores (RFC para timbrar, asignables por punto de venta) ── */}
        {user?.role === 'propietario' && (
          <Section icon={Receipt} title="Emisores (RFC para timbrar)">
            <p className="text-xs text-zinc-500 mb-4">
              Cada punto de venta con facturación habilitada debe tener un emisor asignado (en Puntos de Venta). El CSD de este RFC se carga aparte, directamente en el portal de Facturama.
            </p>

            {emisores.length > 0 && (
              <div className="space-y-2 mb-4">
                {emisores.map(em => (
                  <div key={em.id} className="px-3 py-2 bg-zinc-50 rounded-lg text-sm">
                    <p className="font-mono font-medium text-zinc-800">{em.rfc}</p>
                    <p className="text-xs text-zinc-500">{em.razon_social} · Régimen {em.regimen_fiscal}</p>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={crearEmisor}
              disabled={creandoEmisor || !fiscalCompleto}
              className="w-full h-11 bg-zinc-900 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-crear-emisor"
            >
              {creandoEmisor ? 'Creando...' : 'Crear emisor con estos datos fiscales'}
            </button>
            {emisorError && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{emisorError}
              </p>
            )}
          </Section>
        )}

        {/* ── Font size ── */}
        <Section icon={Type} title="Tamaño de letra">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {FONT_SIZES.map(f => (
              <button
                key={f.id}
                onClick={() => update({ fontSize: f.id })}
                className={`flex flex-col items-center justify-center gap-1 py-4 rounded-xl border-2 transition-all ${
                  prefs.fontSize === f.id
                    ? 'border-zinc-900 bg-zinc-900 text-white'
                    : 'border-zinc-200 bg-zinc-50 text-zinc-700 hover:border-zinc-400'
                }`}
              >
                <span className={`font-bold leading-none ${f.preview}`}
                  style={{ fontSize: { sm: '13px', md: '16px', lg: '20px', xl: '26px' }[f.id] }}>
                  {f.sample}
                </span>
                <span className="text-xs mt-1 opacity-80">{f.label}</span>
                {prefs.fontSize === f.id && <Check className="w-3 h-3 mt-0.5" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-400 mt-3">
            El cambio aplica de inmediato a todo el sistema en este dispositivo.
          </p>
        </Section>

        {/* ── Background color ── */}
        <Section icon={Palette} title="Color de fondo">
          <div className="flex flex-wrap gap-3">
            {BG_THEMES.map(t => (
              <button
                key={t.id}
                onClick={() => update({ bgTheme: t.id })}
                className="flex flex-col items-center gap-1.5"
                title={t.label}
              >
                <span
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-all"
                  style={{
                    backgroundColor: t.color,
                    border: `2px solid ${prefs.bgTheme === t.id ? '#18181b' : t.border}`,
                    boxShadow: prefs.bgTheme === t.id ? '0 0 0 3px #18181b33' : 'none',
                  }}
                >
                  {prefs.bgTheme === t.id && <Check className="w-4 h-4 text-zinc-800" />}
                </span>
                <span className="text-xs text-zinc-600">{t.label}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* ── Thermal printer ── */}
        <Section icon={Printer} title="Impresora térmica">
          {/* Enable toggle */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-medium text-zinc-800 text-sm">Activar impresión de tickets</p>
              <p className="text-xs text-zinc-500">Imprime automáticamente al cerrar ventas y cortes</p>
            </div>
            <button
              onClick={() => setPrinterEnabled(v => !v)}
              className={`relative w-12 h-6 rounded-full transition-colors ${printerEnabled ? 'bg-zinc-900' : 'bg-zinc-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${printerEnabled ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>

          {/* IP + Port */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="col-span-2">
              <label className="text-xs text-zinc-500 mb-1 block">IP de la impresora</label>
              <input
                type="text"
                value={printerIp}
                onChange={e => setPrinterIp(e.target.value)}
                placeholder="192.168.1.100"
                className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:border-zinc-600"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Puerto</label>
              <input
                type="number"
                value={printerPort}
                onChange={e => setPrinterPort(e.target.value)}
                placeholder="9100"
                className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:border-zinc-600"
              />
            </div>
          </div>

          {/* Bridge URL */}
          <div className="mb-3">
            <label className="text-xs text-zinc-500 mb-1 block">URL del puente local</label>
            <input
              type="text"
              value={bridgeUrl}
              onChange={e => setBridgeUrl(e.target.value)}
              placeholder="https://192.168.1.15:3333"
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:border-zinc-600"
            />
            <p className="text-xs text-zinc-400 mt-1">
              IP de la PC donde corre el puente (no localhost si usas tablet).{' '}
              <a
                href={`${bridgeUrl.trim().replace(/\/$/, '')}/cert`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                Instalar certificado
              </a>
            </p>
          </div>

          {/* Test result */}
          {testResult === 'ok' && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm mb-3">
              <Wifi className="w-4 h-4 shrink-0" />
              Impresora conectada. Página de prueba enviada.
            </div>
          )}
          {testResult === 'error' && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-3">
              <WifiOff className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{testError}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={testPrint}
              disabled={testing || !printerIp.trim()}
              className="flex-1 h-11 border border-zinc-300 rounded-lg text-sm font-medium text-zinc-700 flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {testing
                ? <span className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                : <><Printer className="w-4 h-4" /> Prueba de impresión</>
              }
            </button>
            <button
              onClick={savePrinter}
              className="flex-1 h-11 bg-zinc-900 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              {printerSaved ? <><Check className="w-4 h-4" /> Guardado</> : 'Guardar configuración'}
            </button>
          </div>

          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1.5">
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              La impresora debe estar en la misma red y aceptar conexiones RAW en el puerto configurado (generalmente 9100).
            </p>
            <p className="text-xs text-amber-700 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              En tablet/celular: pon la IP de la PC donde corre el puente (ej. https://192.168.1.15:3333), luego toca "Instalar certificado" una sola vez para confiar en él.
            </p>
          </div>
        </Section>

      </div>
    </div>
  );
}
